import { Button } from '@vrplatform/components';
import {
  captureSentryError,
  useDashboard,
  useTrackingEvent,
} from '@vrplatform/data';
import {
  EllipsisMenuItem,
  LoadingIndicator,
} from '@vrplatform/components-legacy';
import {
  showLoadingNotification,
  updateErrorNotification,
  updateSuccessNotification,
} from '@vrplatform/theme';
import { type Maybe, hasValue } from '@vrplatform/utils';
import {
  ActionIcon,
  Box,
  Center,
  Group,
  Modal,
  Text,
  useMantineColorScheme,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useId, useState } from 'react';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { Icon } from '@vrplatform/icons';
import type { OwnerStatementStatus, TaxStatementStatus } from '@vrplatform/log';
import {
  downloadFileFromUrl,
  downloadFromUrl,
  ensurePdfWorker,
} from '../utils';
import { zipUrls } from '../views/statements/legacy/_hooks/useStatementCsvMultipleExport';

ensurePdfWorker();

export const PdfPreviewModal = ({
  closeModal,
  opened,
  entity,
  type,
}: {
  opened: boolean;
  closeModal: () => void;
} & (
  | {
      type: 'tax_statement';
      entity: {
        urls: Maybe<string>[];
        status: TaxStatementStatus;
        id: Maybe<string>;
      } | null;
    }
  | {
      type: 'gl_owner_statement';
      entity: {
        urls: Maybe<string>[];
        status: OwnerStatementStatus;
        id: Maybe<string>;
      } | null;
    }
)) => {
  const track = useTrackingEvent();
  const [dashboard] = useDashboard();
  const [debounced] = useDebouncedValue(entity, 500, {
    leading: true,
  });

  const notifyId = useId();

  const [activeUrlIndex, setActiveUrlIndex] = useState(0);

  const [numPages, setNumPages] = useState<number>();
  const { colorScheme } = useMantineColorScheme();

  function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
    setNumPages(numPages);
  }

  const urls = (debounced?.urls || entity?.urls)?.filter(hasValue);
  const url = urls?.[activeUrlIndex];
  const filename =
    type === 'gl_owner_statement' ? 'owner-statement.pdf' : 'tax-statement.pdf';

  const trackPdfInteraction = (trackedUrls?: string[]) => {
    const user_type = dashboard === 'owner' ? 'owner' : 'property_manager';

    if (type === 'tax_statement') {
      track('tax_statement_downloaded', {
        taxStatementStatus: entity?.status as any,
        taxStatementId: entity?.id,
        taxStatementUrl: url,
        userType: user_type,
      });
      return;
    }

    track('gl_owner_statement_downloaded', {
      ownerStatementId: entity?.id,
      ownerStatementUrls: trackedUrls || urls,
      userType: user_type,
    });
  };

  const openInNewTab = () => {
    try {
      if (!url) throw new Error('No PDF found');

      trackPdfInteraction(url ? [url] : undefined);
      downloadFromUrl(url, filename, {
        disposition: 'openInNewTab',
      });

      updateSuccessNotification({
        id: notifyId,
        title: 'PDF Export',
        message: 'PDF opened in a new tab.',
      });
    } catch (error: any) {
      captureSentryError(error);
      updateErrorNotification({
        id: notifyId,
        title: 'PDF Open Error',
        message:
          error?.message ||
          'An error occurred while opening the PDF. Please try again.',
      });
    }
  };

  const downloadUrls = async () => {
    showLoadingNotification({
      id: notifyId,
    });

    try {
      if (!urls?.length) throw new Error('No PDFs found');

      trackPdfInteraction(urls);

      if (urls.length > 1) {
        await zipUrls(
          urls.map((x, index) => ({
            url: x,
            filename: `${entity?.id || notifyId}_${index}`,
          })),
          'pdf'
        );
      } else {
        await downloadFileFromUrl(urls[0], filename);
      }

      updateSuccessNotification({
        id: notifyId,
        title: 'PDF Export',
        message:
          urls.length > 1
            ? 'PDFs downloaded successfully.'
            : 'PDF downloaded successfully.',
      });
    } catch (error: any) {
      captureSentryError(error);
      updateErrorNotification({
        id: notifyId,
        title: 'PDF Download Error',
        message:
          error?.message ||
          'An error occurred while downloading the PDF. Please try again.',
      });
    }
  };

  return (
    <Modal
      opened={opened}
      size={820}
      radius="md"
      onClose={closeModal}
      title={
        <Group justify="space-between" w="100%" pr="xs">
          <Box>
            <Text
              component="span"
              display="block"
              c={colorScheme === 'dark' ? 'dimmed' : 'gray'}
              size="xs"
            >
              PDF Preview
            </Text>{' '}
            <Text fw={500}>
              {type === 'gl_owner_statement'
                ? 'Owner Statement'
                : 'Tax Statement'}
            </Text>
          </Box>
          {url && (
            <Button
              leftIcon={'FileDownloadIcon'}
              size="xs"
              menu={{
                trigger: 'click',
                items: (
                  <>
                    <EllipsisMenuItem
                      icon="ExternalLinkIcon"
                      onClick={openInNewTab}
                    >
                      Open PDF in new tab
                    </EllipsisMenuItem>
                    <EllipsisMenuItem
                      icon="FileDownloadIcon"
                      onClick={downloadUrls}
                    >
                      {urls?.length && urls.length > 1
                        ? 'Download PDFs'
                        : 'Download PDF'}
                    </EllipsisMenuItem>
                  </>
                ),
              }}
            >
              Download
            </Button>
          )}
        </Group>
      }
      centered
      styles={{
        title: {
          fontWeight: 500,
          flex: 1,
        },
      }}
    >
      {(urls?.length || 0) > 1 && (
        <Group justify="space-between" gap={5} w="100%">
          <ActionIcon
            disabled={!activeUrlIndex}
            variant="subtle"
            color="gray"
            onClick={() => setActiveUrlIndex((e) => e - 1)}
            sx={{
              transform: 'rotate(90deg)',
              ':active': {
                transform: 'rotate(90deg) translate(1px)',
              },
            }}
          >
            <Icon icon="ChevronIcon" size={20} />
          </ActionIcon>
          {!!urls?.length && (
            <Text size="xs" c="gray" component="span">
              File {activeUrlIndex + 1} of {urls?.length}
            </Text>
          )}
          <ActionIcon
            disabled={!urls || activeUrlIndex === urls.length - 1}
            onClick={() => setActiveUrlIndex((e) => e + 1)}
            variant="subtle"
            color="gray"
            sx={{
              transform: 'rotate(-90deg)',
              ':active': {
                transform: 'rotate(-90deg) translate(-1px)',
              },
            }}
          >
            <Icon icon="ChevronIcon" size={20} />
          </ActionIcon>
        </Group>
      )}

      <Center mih="40vh" miw={700}>
        {url ? (
          <Document
            loading={<LoadingIndicator size="md" />}
            file={url}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={(error) => {
              captureSentryError(error);
            }}
          >
            {Array.from(new Array(numPages), (_, index) => (
              <Page
                key={`page_${index + 1}`}
                pageNumber={index + 1}
                width={800}
              />
            ))}
          </Document>
        ) : (
          <Text>Missing PDF. Unable to load preview.</Text>
        )}
      </Center>
    </Modal>
  );
};
